import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import CurriculumRequirementCard from "../components/curriculumRequirements/CurriculumRequirementCard";
import CreateCurriculumRequirementModal from "../components/curriculumRequirements/CreateCurriculumRequirementModal";
import UpdateCurriculumRequirementModal from "../components/curriculumRequirements/UpdateCurriculumRequirementModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getCurriculumRequirements,
  deleteCurriculumRequirement,
  resetCurriculumRequirementServerStatus,
} from "../redux/actions/curriculumRequirementActions";
import { CurriculumRequirementResponseDto } from "../shared/dtos/curriculum/CurriculumRequirementResponseDto";

const CurriculumRequirementsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux Selectors ---
  const {
    items: curriculumRequirements,
    loadingFetchAll: loadingCurriculumRequirements,
    errorFetchAll: errorLoadingCurriculumRequirements,
    serverStatus,
    loadingCreate: loadingCurriculumRequirementCreate,
    errorCreate: errorCreatingCurriculumRequirement,
    loadingUpdate: loadingCurriculumRequirementUpdate,
    errorUpdate: errorUpdatingCurriculumRequirement,
    loadingDelete: loadingCurriculumRequirementDelete,
  } = useAppSelector((state) => state.curriculumRequirement);

  // --- Local Search & Filter State ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedRequirement, setSelectedRequirement] =
    useState<CurriculumRequirementResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("curriculum_requirement:create");
  const canUpdate = hasPermission("curriculum_requirement:update");
  const canDelete = hasPermission("curriculum_requirement:delete");

  // Fetch Data (Optionally passing branchId for super admin)
  const fetchRequirementsList = useCallback((): void => {
    dispatch(
      getCurriculumRequirements(
        selectedBranchId ? { branchId: selectedBranchId } : undefined,
      ),
    );
  }, [dispatch, selectedBranchId]);

  useEffect(() => {
    fetchRequirementsList();
  }, [fetchRequirementsList]);

  // Frontend Filter
  const filteredRequirements = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return curriculumRequirements.filter((req) => {
      const matchesBranch = selectedBranchId
        ? req.branchId === selectedBranchId
        : true;

      if (!matchesBranch) return false;
      if (!query) return true;

      const matchesSubject = req.subjectName?.toLowerCase().includes(query);
      const matchesGrade = req.gradeLevelName?.toLowerCase().includes(query);
      const matchesAcademicYear = req.academicYearName
        ?.toLowerCase()
        .includes(query);

      return Boolean(matchesSubject || matchesGrade || matchesAcademicYear);
    });
  }, [curriculumRequirements, searchTerm, selectedBranchId]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingCurriculumRequirementCreate) setIsCreateModalOpen(false);
  }, [loadingCurriculumRequirementCreate]);

  const handleOpenUpdate = (item: CurriculumRequirementResponseDto): void => {
    setSelectedRequirement(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingCurriculumRequirementUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedRequirement(null);
    }
  }, [loadingCurriculumRequirementUpdate]);

  const handleOpenDelete = (item: CurriculumRequirementResponseDto): void => {
    setSelectedRequirement(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingCurriculumRequirementDelete) {
      setIsDeleteModalOpen(false);
      setSelectedRequirement(null);
    }
  }, [loadingCurriculumRequirementDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedRequirement) {
      dispatch(deleteCurriculumRequirement(selectedRequirement.id));
    }
  };

  // Auto-close modals on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetCurriculumRequirementServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Curriculum Requirements"
      subtitle="Manage lessons per week and requirements for subjects per grade level."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Requirement
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by subject, grade, or academic year..."
      >
        {/* Rendered only for SUPER_ADMIN */}
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => setSelectedBranchId(bId)}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingCurriculumRequirements}
        error={errorLoadingCurriculumRequirements}
        itemCount={filteredRequirements.length}
        emptyMessage="No curriculum requirements match your search criteria"
        onRetry={fetchRequirementsList}
      >
        {filteredRequirements.map((req) => (
          <CurriculumRequirementCard
            key={req.id}
            requirement={req}
            onEdit={canUpdate ? () => handleOpenUpdate(req) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(req) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateCurriculumRequirementModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingCurriculumRequirementCreate}
          error={errorCreatingCurriculumRequirement}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedRequirement && (
        <UpdateCurriculumRequirementModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          requirement={selectedRequirement}
          loading={loadingCurriculumRequirementUpdate}
          error={errorUpdatingCurriculumRequirement}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedRequirement && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Curriculum Requirement"
          description={`Are you sure you want to delete the curriculum requirement for "${selectedRequirement.subjectName}" (${selectedRequirement.gradeLevelName})? This action cannot be undone.`}
          loading={loadingCurriculumRequirementDelete}
        />
      )}
    </PageContainer>
  );
};

export default CurriculumRequirementsPage;
